// Risk Card Component
// Date: December 4, 2025
// Purpose: Card view for Risk entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarIcon,
  UserIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useLanguage } from '@/state/language/LanguageProvider';

export interface RiskCardProps {
  risk: {
    id: string;
    name?: string;
    description?: string;
    risk_status?: string;
    risk_level?: string;
    risk_category?: string;
    owner_name?: string;
    due_date?: string;
    mitigation_status?: string;
    [key: string]: any;
  };
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onCopy?: (item: any) => void;
  onClick?: (id: string) => void;
  canEdit?: (item: any) => boolean;
  customActions?: Array<{
    label: string | ((item: any) => string);
    icon: React.ComponentType<any> | ((item: any) => React.ComponentType<any>);
    onClick: (item: any) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: any) => 'primary' | 'secondary' | 'danger');
  }>;
}

// Helper function to get risk category name from risk object
const getRiskCategoryName = (risk: RiskCardProps['risk']): string => {
  if (risk.risk_category) return risk.risk_category;
  if (typeof risk.risk_category_id === 'object' && risk.risk_category_id?.name) {
    return risk.risk_category_id.name;
  }
  return '';
};

export const RiskCard: React.FC<RiskCardProps> = ({
  risk,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onClick,
  canEdit,
  customActions = []
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleClick = () => {
    if (onClick) {
      onClick(risk.id);
    } else if (onView) {
      onView(risk);
    } else {
      navigate(`/risk/${risk.id}`);
    }
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const getRiskLevelColor = (level?: string) => {
    switch (level?.toUpperCase()) {
      case 'CRITICAL':
      case 'HIGH':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)] border-[var(--color-error)]/30';
      case 'MEDIUM':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'LOW':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'IDENTIFIED':
        return 'bg-[var(--color-info)]/20 text-[var(--color-info)] border-[var(--color-info)]/30';
      case 'ASSIGNED':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)] border-[var(--color-warning)]/30';
      case 'ACCEPTED':
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
      case 'MITIGATION_IN_PROGRESS':
        return 'bg-[var(--color-info)]/20 text-[var(--color-info)] border-[var(--color-info)]/30';
      case 'MITIGATION_COMPLETED':
        return 'bg-[var(--color-success)]/20 text-[var(--color-success)] border-[var(--color-success)]/30';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-[var(--color-border)]';
    }
  };

  return (
    <Card
      variant="outline"
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col border border-[var(--color-border)] rounded-lg bg-[var(--color-surface-bg)]"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col min-h-0">
        {/* Top: status */}
        {(risk.risk_status || risk.status) && (
          <div className="mb-2">
            <Badge className={`text-xs border ${getStatusColor(risk.risk_status || risk.status)}`}>
              {(risk.risk_status || risk.status || '').replace(/_/g, ' ')}
            </Badge>
          </div>
        )}
        {/* Title + actions */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-semibold text-[var(--color-secondary-text)] text-base line-clamp-1 flex-1 min-w-0">
            {risk.name || 'Risk'}
          </h3>
          <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {onView && (
              <button
                onClick={(e) => handleActionClick(e, () => onView(risk))}
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title={t('common.view')}
                type="button"
              >
                <EyeIcon className="w-4 h-4" />
              </button>
            )}
            {onEdit && (canEdit ? canEdit(risk) : true) && (
              <button
                onClick={(e) => handleActionClick(e, () => onEdit(risk))}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                title={t('common.edit')}
                type="button"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {onCopy && (
              <button
                onClick={(e) => handleActionClick(e, () => onCopy(risk))}
                className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                title={t('common.copy')}
                type="button"
              >
                <DocumentDuplicateIcon className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => handleActionClick(e, () => onDelete(risk))}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                title={t('common.delete')}
                type="button"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
            {customActions.map((action, index) => {
              const IconComponent = typeof action.icon === 'function' && action.icon.length > 0
                ? (action.icon as (item: any) => React.ComponentType<any>)(risk)
                : action.icon as React.ComponentType<any>;
              const variant = typeof action.variant === 'function'
                ? action.variant(risk)
                : action.variant || 'primary';
              const colorClass = variant === 'danger'
                ? 'text-red-600 hover:bg-red-50'
                : variant === 'secondary'
                  ? 'text-gray-600 hover:bg-gray-50'
                  : 'text-blue-600 hover:bg-blue-50';
              return (
                <button
                  key={index}
                  onClick={(e) => handleActionClick(e, () => action.onClick(risk))}
                  className={`p-1.5 ${colorClass} rounded-md transition-colors`}
                  title={typeof action.label === 'function' ? action.label(risk) : action.label}
                  type="button"
                >
                  <IconComponent className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Description: prominent block with more space */}
        <div className="flex-1 min-h-[4.5rem] mb-3">
          {risk.description ? (
            <p className="text-sm text-[var(--color-secondary-text)] leading-relaxed line-clamp-5 break-words">
              {risk.description}
            </p>
          ) : (
            <p className="text-sm text-[var(--color-secondary-text)] opacity-60 italic">No description</p>
          )}
        </div>

        {/* Bottom: category, severity, probability, mitigation (impact/importance/urgency) */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {getRiskCategoryName(risk) && (
            <Badge variant="ghost" className="text-xs">
              {getRiskCategoryName(risk)}
            </Badge>
          )}
          {(risk.severity || risk.risk_level) && (
            <Badge className={`text-xs border ${getRiskLevelColor((risk.severity || risk.risk_level || '').toUpperCase())}`}>
              {(risk.severity || risk.risk_level || '').toString().toUpperCase()}
            </Badge>
          )}
          {(risk.probability || '').trim() && (
            <Badge className={`text-xs border ${getRiskLevelColor((risk.probability || '').toUpperCase())}`}>
              {(risk.probability || '').replace(/_/g, ' ')}
            </Badge>
          )}
          {risk.mitigation_status && (
            <Badge variant="outline" className="text-xs">
              {risk.mitigation_status.replace(/_/g, ' ')}
            </Badge>
          )}
        </div>

        {/* Metadata footer */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--color-secondary-text)] opacity-80 border-t border-[var(--color-border)] pt-2 mt-auto">
          {risk.owner_name && (
            <span className="flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5" />
              {risk.owner_name}
            </span>
          )}
          {risk.due_date && (
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5" />
              Due {new Date(risk.due_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

